// Demandes de congé — pending Lamis→Bouchra requests as decision cards
// (proto CgDemandesView), with a recent-movements side panel. Approve
// debits the balance atomically server-side; the exit animation delays
// the mutation by ~340 ms so the card can play out.
import { useState } from 'react';
import { toast } from '../../store/toast.js';
import {
  useApproveRequest, useLeaveTransactions, useRejectRequest,
  type LeaveBalanceRow, type LeaveRequestDTO,
} from '../../api/hooks.js';
import { cgAvColor, cgD, cgFmtRange, cgInitials, cgNum, cgToday, entityById } from './common.js';

const TX_LABEL: Record<string, string> = {
  INITIAL_STOCK: 'Stock initial',
  MONTHLY_ACCRUAL: 'Acquisition',
  MANUAL_ADDITION: 'Ajout manuel',
  CONSUMPTION_DIRECT: 'Congé saisi',
  CONSUMPTION_VIA_REQUEST: 'Demande débitée',
};

export function DemandesView({ pending, balances, recupBalances, onOpen }: {
  pending: LeaveRequestDTO[];
  balances: Map<string, LeaveBalanceRow>;
  // RECUP-typed requests (Espace employé) debit the récup stock — the
  // "solde après" fact must read the matching balance.
  recupBalances?: Map<string, LeaveBalanceRow>;
  onOpen: (empId: string) => void;
}) {
  const approve = useApproveRequest();
  const reject = useRejectRequest();
  const txs = useLeaveTransactions({ type: 'CONGE' });
  const [exiting, setExiting] = useState<Record<string, 'valide' | 'refuse'>>({});
  const today = cgToday();

  const decide = (req: LeaveRequestDTO, dec: 'valide' | 'refuse') => {
    setExiting((x) => ({ ...x, [req.id]: dec }));
    window.setTimeout(async () => {
      try {
        if (dec === 'valide') {
          await approve.mutateAsync(req.id);
          toast.success('Demande approuvée · solde débité');
        } else {
          await reject.mutateAsync({ id: req.id });
          toast.success('Demande refusée');
        }
      } catch (e) {
        setExiting((x) => { const { [req.id]: _drop, ...rest } = x; return rest; });
        const b = (e as Error & { body?: { error?: string } }).body;
        toast.danger(b?.error === 'insufficient_balance' ? 'Solde insuffisant pour approuver.'
          : b?.error === 'invalid_stage' ? 'Demande déjà traitée.' : 'Échec — réessayez.');
      }
    }, 340);
  };

  const recent = (txs.data ?? []).filter((t) => t.kind.startsWith('CONSUMPTION')).slice(0, 10);

  return (
    <div className="cg-dem view-enter">
      <div className="cg-dem-main">
        <div className="cg-secttl">En attente<span className="gm-th-count">{pending.length}</span></div>
        {pending.length === 0 && <div className="cg-empty">🎉 Aucune demande en attente.</div>}
        <div className="cg-cards">
          {pending.map((req, i) => {
            const isRecup = req.type === 'RECUP';
            const bal = (isRecup ? recupBalances : balances)?.get(req.employee.id);
            const stock = bal?.balance.current ?? 0;
            const after = stock - req.daysCount;
            const over = after < 0;
            const notice = Math.max(0, Math.round((cgD(req.startDate).getTime() - cgD(today).getTime()) / 86400000));
            const ent = entityById(req.employee.entityId);
            return (
              <div key={req.id} className={'cg-card' + (exiting[req.id] ? ' out ' + exiting[req.id] : '')} style={{ animationDelay: `${i * 40}ms` }}>
                <div className="cg-card-h">
                  <span className="gm-av" style={{ background: cgAvColor(req.employee.id), cursor: 'pointer' }} onClick={() => onOpen(req.employee.id)}>
                    {cgInitials(req.employee.name)}
                  </span>
                  <div className="cg-card-id">
                    <b>{req.employee.name}</b>
                    <i>
                      {ent && <span className="cg-entdot" style={{ background: ent.color }} />}
                      {ent?.short ?? req.employee.entityId}{req.proposedByName ? ` · proposé par ${req.proposedByName}` : ''}
                    </i>
                  </div>
                  <span className="cg-typechip" style={isRecup
                    ? { background: 'var(--infos)', color: 'var(--infoc)' }
                    : { background: 'var(--shfs)', color: 'var(--shfc)' }}>
                    {isRecup ? 'Récupération' : 'Congé annuel'}
                  </span>
                </div>
                <div className="cg-card-dates">
                  <span className="cg-card-range">{cgFmtRange(req.startDate, req.endDate)}</span>
                  <span className="cg-card-days gm-mono">{cgNum(req.daysCount)} j ouvrable{req.daysCount > 1 ? 's' : ''}</span>
                </div>
                {req.reason && <div className="cg-card-note">« {req.reason} »</div>}
                <div className="cg-card-facts">
                  <div className={'cg-fact' + (over ? ' bad' : '')}>
                    <span>Solde après</span>
                    <b className="gm-mono">{cgNum(stock)} → {cgNum(after)} j</b>
                  </div>
                  <div className={'cg-fact' + (notice < 3 ? ' warn' : '')}>
                    <span>Préavis</span>
                    <b>{notice} j</b>
                  </div>
                  <div className="cg-fact">
                    <span>Période</span>
                    <b>{req.startDate === req.endDate ? '1 jour' : `${cgNum(req.daysCount)} j ouvrés`}</b>
                  </div>
                </div>
                <div className="cg-card-act">
                  <button className="cg-refuse" disabled={!!exiting[req.id]} onClick={() => decide(req, 'refuse')}>Refuser</button>
                  <button className="cg-approve" disabled={!!exiting[req.id] || over} onClick={() => decide(req, 'valide')}>
                    <span>✓</span> Approuver
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="cg-dem-side">
        <div className="cg-secttl">Traité récemment</div>
        <div className="cg-histlist">
          {recent.map((t) => {
            const emp = balances.get(t.employeeId)?.employee;
            return (
              <div key={t.id} className="cg-hist" onClick={() => onOpen(t.employeeId)}>
                <span className="cg-hist-st debit gm-mono">{cgNum(t.amount)}</span>
                <div className="cg-hist-m">
                  <b>{emp?.name ?? '—'}</b>
                  <i>{TX_LABEL[t.kind] ?? t.kind} · {t.date}</i>
                </div>
                <span className="cg-hist-by">{t.source}</span>
              </div>
            );
          })}
          {recent.length === 0 && <div className="cg-empty sm">Aucun mouvement récent.</div>}
        </div>
      </div>
    </div>
  );
}
